import { ChangeEvent, ReactElement } from 'react';
import { tokens, visuallyHiddenStyle } from '../../theme';
import type { FormFieldState } from '../FormField/FormField';

const FIELD_GAP = 4;
const FIELD_HEIGHT = 201;
// Fixo nos dois breakpoints no Figma (mobile e desktop usam p-[8px]).
const FIELD_PADDING = 8;

/** Classe utilitária definida em src/index.css — cor do placeholder não é alcançável via style inline. */
const INPUT_CLASS_NAME = 'field-input';

const BORDER_COLOR_BY_STATE: Record<FormFieldState, string> = {
  default: tokens.colors.brand.gray.light850,
  valid: tokens.colors.brand.green.DEFAULT,
  error: tokens.colors.brand.red.DEFAULT,
};

type TextAreaFieldProps = {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  maxLength: number;
  state: FormFieldState;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
};

export const TextAreaField = ({
  id,
  label,
  value,
  placeholder,
  maxLength,
  state,
  onChange,
  onBlur,
  error,
}: TextAreaFieldProps): ReactElement => {
  const counterId = `${id}-counter`;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div style={styles.field}>
      <div style={styles.fieldBox}>
        <label htmlFor={id} style={styles.hiddenLabel}>
          {label}
        </label>
        <textarea
          id={id}
          className={INPUT_CLASS_NAME}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-invalid={state === 'error'}
          aria-describedby={[errorId, counterId].filter(Boolean).join(' ')}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
          onBlur={onBlur}
          style={
            {
              ...styles.textarea,
              borderColor: BORDER_COLOR_BY_STATE[state],
              '--placeholder-color': tokens.colors.brand.gray.light250,
            } as React.CSSProperties
          }
        />
      </div>
      <div style={styles.footer}>
        <p id={errorId} role={error ? 'alert' : undefined} style={styles.error}>
          {error}
        </p>
        <p id={counterId} style={styles.counter}>
          {value.length}/{maxLength} caracteres
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: FIELD_GAP,
    width: '100%',
    boxSizing: 'border-box',
  },
  fieldBox: {
    width: '100%',
  },
  hiddenLabel: visuallyHiddenStyle,
  textarea: {
    height: FIELD_HEIGHT,
    width: '100%',
    boxSizing: 'border-box',
    padding: FIELD_PADDING,
    borderRadius: tokens.radius.small,
    border: `${tokens.borderWidth.thick}px solid`,
    backgroundColor: tokens.colors.global.white,
    fontSize: tokens.typography.fontSize.input,
    fontFamily: 'inherit',
    color: tokens.colors.global.black,
    resize: 'vertical',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  error: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.disclaimer,
    color: tokens.colors.brand.red.DEFAULT,
  },
  counter: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.counter,
    color: tokens.colors.brand.gray.light450,
  },
};
